import {
  ArrowSquareIn,
  ArrowSquareOut,
  Bell,
  CheckCircle,
  Envelope,
  Globe,
  PaperPlaneTilt,
  Plugs,
  Warning,
  WhatsappLogo,
  XCircle,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

interface TelegramStatus {
  configured: boolean;
  bot: string | null;
  linked: boolean;
  username: string | null;
}

type Tone = "on" | "off" | "warn" | "na";

/**
 * Channels (2026-10-17): the whole picture on one page. Left, what comes IN and is read for what
 * matters (mail, Telegram texts, web sources). Right, how Docit reaches you OUT (the bell, e-mail,
 * Telegram, webhooks). Every row says its live state in words and points at the one place it is set
 * up. What cannot be offered says why, instead of a greyed-out button.
 */
export function Channels() {
  const { t } = useTranslation();
  const api = useApi();
  const mail = useQuery({
    queryKey: ["mail-reading", "settings"],
    queryFn: () => api.mailReading.settings.get(),
    retry: false,
  });
  const telegram = useQuery({
    queryKey: ["work", "telegram", "status"],
    queryFn: async () => (await api.research.post("work.telegram.status", {})) as TelegramStatus,
    retry: false,
  });
  const webhooks = useQuery({
    queryKey: ["automation", "webhooks"],
    queryFn: () => api.governance.webhooks.list(),
    retry: false,
  });
  const prefs = useQuery({
    queryKey: ["notifications", "preferences"],
    queryFn: () => api.notifications.preferences(),
    retry: false,
  });

  if (mail.isLoading || telegram.isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  const m = mail.data;
  const tg = telegram.data;
  const mailboxes = (m?.sources.connections.length ?? 0) + (m?.sources.imap.length ?? 0);
  const emailOnSomewhere = prefs.data
    ? Object.values(prefs.data as Record<string, unknown>).some(
        (v) => v && typeof v === "object" && (v as { email?: boolean }).email === true,
      )
    : false;
  const webhookCount = webhooks.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <Text variant="small">
        {t(
          "What comes in is read for what matters — importance, meaning, the one thing to do — and your rules decide what happens next: tell you, file it, hand it to a flow. What goes out reaches you where you are.",
        )}
      </Text>

      <section aria-labelledby="ch-in">
        <h3 id="ch-in" className="flex items-center gap-2 text-h3 text-text">
          <ArrowSquareIn size={18} aria-hidden />
          {t("Input channels")}
        </h3>
        <ul className="mt-2 divide-y divide-divider rounded-md border border-divider">
          <Row
            icon={Envelope}
            title={t("Mail")}
            tone={mailboxes > 0 ? "on" : "warn"}
            state={
              mailboxes > 0
                ? t("{{count}} mailboxes connected — read every couple of minutes", {
                    count: mailboxes,
                  })
                : t("No mailbox connected yet — paste a mail, or connect Gmail, Outlook or IMAP")
            }
            detail={t(
              "Gmail / Outlook, any IMAP mailbox, a pasted mail, or a forwarding address for services.",
            )}
            links={[
              { to: "/mail/settings", label: t("Mail settings") },
              { to: "/mail", label: t("Open Mail") },
            ]}
          />
          <Row
            icon={PaperPlaneTilt}
            title={t("Telegram — texts to the bot")}
            tone={!tg?.configured ? "warn" : tg.linked ? "on" : "warn"}
            state={
              !tg?.configured
                ? t("The bot is not set up on this server yet")
                : tg.linked
                  ? t("Linked as @{{username}} — a text to the bot is read like a mail", {
                      username: tg.username ?? "",
                    })
                  : t("Bot ready — link your chat once and your texts become items")
            }
            detail={t(
              "Send or forward anything to the bot: it is read, and your rules run over it (file it, tell you, run a flow).",
            )}
            links={[
              { to: "/settings/$section", params: { section: "telegram" }, label: t("Telegram") },
            ]}
          />
          <Row
            icon={Globe}
            title={t("Web sources")}
            tone="na"
            state={t("Pages and feeds the workspace watches")}
            detail={t("What a watched page says becomes knowledge; rules over it come later.")}
            links={[
              {
                to: "/settings/$section",
                params: { section: "web-sources" },
                label: t("Web sources"),
              },
            ]}
          />
        </ul>
      </section>

      <section aria-labelledby="ch-out">
        <h3 id="ch-out" className="flex items-center gap-2 text-h3 text-text">
          <ArrowSquareOut size={18} aria-hidden />
          {t("Output channels")}
        </h3>
        <ul className="mt-2 divide-y divide-divider rounded-md border border-divider">
          <Row
            icon={Bell}
            title={t("The bell")}
            tone="on"
            state={t("Always on — every notification lands here first")}
            detail={t("Per event you choose what also goes further: e-mail, Telegram.")}
            links={[
              {
                to: "/settings/$section",
                params: { section: "notifications" },
                label: t("Notifications"),
              },
            ]}
          />
          <Row
            icon={Envelope}
            title={t("E-mail")}
            tone={emailOnSomewhere ? "on" : "off"}
            state={
              emailOnSomewhere
                ? t("On for some events")
                : t("Off for everything but invitations and sign-in — turn it on per event")
            }
            detail={t("Nothing e-mails you unasked; each event has its own switch.")}
            links={[
              {
                to: "/settings/$section",
                params: { section: "notifications" },
                label: t("Notifications"),
              },
            ]}
          />
          <Row
            icon={PaperPlaneTilt}
            title={t("Telegram")}
            tone={!tg?.configured ? "warn" : tg.linked ? "on" : "warn"}
            state={
              !tg?.configured
                ? t("The bot is not set up on this server yet")
                : tg.linked
                  ? t(
                      "Linked as @{{username}} — questions, mail alerts and reminders arrive there",
                      {
                        username: tg.username ?? "",
                      },
                    )
                  : t("Bot ready — link your chat once")
            }
            detail={t(
              "Work questions and mail alerts always; reminders and any other event when its Telegram switch is on.",
            )}
            links={[
              { to: "/settings/$section", params: { section: "telegram" }, label: t("Telegram") },
              {
                to: "/settings/$section",
                params: { section: "telegram" },
                search: { help: "setup" },
                label: t("How to set up Telegram"),
              },
            ]}
          />
          <Row
            icon={Plugs}
            title={t("Webhooks")}
            tone={webhookCount > 0 ? "on" : "off"}
            state={
              webhooks.isError
                ? t("Managed by a workspace administrator")
                : webhookCount > 0
                  ? t("{{count}} endpoints receive events", { count: webhookCount })
                  : t("No endpoint yet")
            }
            detail={t("Workspace events to your own systems — Zapier, Make, a script.")}
            links={[
              { to: "/settings/$section", params: { section: "webhooks" }, label: t("Webhooks") },
            ]}
          />
          <Row
            icon={WhatsappLogo}
            title={t("WhatsApp")}
            tone="na"
            state={t("Being connected — the same shape as Telegram")}
            detail={t(
              "Meta's Business API only allows free text within 24 hours of your last message, so it will carry questions and replies first.",
            )}
            links={[]}
          />
        </ul>
      </section>

      <Text variant="small">
        {t(
          "Connected workspaces — editor windows, repositories, documentation folders and apps — have their own group below: they are places Docit works in, not channels it talks through.",
        )}
      </Text>
    </div>
  );
}

function Row({
  icon: Icon,
  title,
  tone,
  state,
  detail,
  links,
}: {
  icon: typeof Bell;
  title: string;
  tone: Tone;
  state: string;
  detail: string;
  links: {
    to: "/mail" | "/mail/settings" | "/settings/$section";
    params?: { section: string };
    search?: { help: "setup" };
    label: string;
  }[];
}) {
  const ToneIcon =
    tone === "on" ? CheckCircle : tone === "warn" ? Warning : tone === "off" ? XCircle : Globe;
  const toneClass =
    tone === "on"
      ? "text-[color:var(--success)]"
      : tone === "warn"
        ? "text-[color:var(--warning)]"
        : "text-text-tertiary";
  return (
    <li className="flex flex-wrap items-start gap-3 px-3 py-2.5">
      <Icon size={20} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body font-medium text-text">{title}</span>
          <span className={`inline-flex items-center gap-1 text-small ${toneClass}`}>
            <ToneIcon size={14} weight={tone === "na" ? "regular" : "fill"} aria-hidden />
            {state}
          </span>
        </div>
        <p className="mt-0.5 text-small text-text-tertiary">{detail}</p>
        {links.length > 0 ? (
          <p className="mt-1 flex flex-wrap gap-x-3 text-small">
            {links.map((l) => (
              <LinkTo key={l.label} link={l} />
            ))}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function LinkTo({
  link,
}: {
  link: {
    to: "/mail" | "/mail/settings" | "/settings/$section";
    params?: { section: string };
    search?: { help: "setup" };
    label: string;
  };
}): ReactNode {
  if (link.to === "/settings/$section") {
    return (
      <Link
        to="/settings/$section"
        params={link.params ?? { section: "channels" }}
        search={link.search}
        className="text-link underline"
      >
        {link.label}
      </Link>
    );
  }
  return (
    <Link to={link.to} className="text-link underline">
      {link.label}
    </Link>
  );
}
